import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDealById } from '../services/apiService';
import Logo from '../components/Logo';
import './DealPreviewPage.css';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function expiryState(expiresAtIso) {
  if (!expiresAtIso) return { expired: false, text: '⏰ Deal verloopt vandaag om 23:59' };
  const end = new Date(expiresAtIso).getTime();
  if (end <= Date.now()) return { expired: true, text: 'Deze deal is verlopen' };
  const sameDay = new Date(expiresAtIso).toDateString() === new Date().toDateString();
  if (sameDay) return { expired: false, text: '⏰ Deal verloopt vandaag om 23:59' };
  const d = new Date(expiresAtIso);
  return {
    expired: false,
    text: `⏰ Geldig tot ${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })} 23:59`,
  };
}

export default function DealPreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showInstall, setShowInstall] = useState(false);

  const [deal, setDeal] = useState(null);

  // Shared links are opened by people who may not have the app, so this has
  // to come from the backend — there is no local copy to fall back on.
  useEffect(() => {
    let cancelled = false;
    getDealById(id)
      .then((d) => { if (!cancelled) setDeal(d); })
      .catch(() => { if (!cancelled) setDeal(null); });
    return () => { cancelled = true; };
  }, [id]);

  const standalone = isStandalone();
  const ios = isIOS();

  const handleInstallClick = () => {
    if (standalone) {
      navigate(`/deal/${id}`);
      return;
    }
    setShowInstall(true);
  };

  const expiry = expiryState(deal?.expiresAt);

  return (
    <div className="dpreview">
      <header className="dpreview__header">
        <Logo size={26} className="dpreview__logo" />
        <p className="dpreview__sub">Gedeeld via WalkIn</p>
      </header>

      <main className="dpreview__main">
        {deal ? (
          <div className="dpreview__card">
            <div className="dpreview__hero">
              <span className="dpreview__limited">Limited deal</span>
            </div>
            <h1 className="dpreview__title">{deal.title}</h1>
            <div className="dpreview__shop-row">
              <span className="dpreview__shop">{deal.shopName}</span>
              <span className="dpreview__dot">·</span>
              <span className="dpreview__area">{deal.area}</span>
            </div>
            {deal.description && (
              <p className="dpreview__desc">{deal.description}</p>
            )}
            {deal.category && (
              <div className="dpreview__tags">
                <span className="dpreview__tag">{deal.category}</span>
              </div>
            )}
            <div
              className={
                'dpreview__expiry' +
                (expiry.expired ? ' dpreview__expiry--expired' : '')
              }
            >
              {expiry.text}
            </div>
            {expiry.expired && (
              <div className="dpreview__expired-banner">
                Helaas — deze deal is niet meer geldig.
                Installeer WalkIn voor de nieuwste deals!
              </div>
            )}
          </div>
        ) : (
          <div className="dpreview__card dpreview__card--missing">
            <h1 className="dpreview__title">Deze deal wacht op je</h1>
            <p className="dpreview__desc">
              Iemand heeft een WalkIn deal met je gedeeld. Installeer
              WalkIn op je telefoon en open de link opnieuw om de deal
              te bekijken en in te leveren.
            </p>
          </div>
        )}

        <section className="dpreview__gate">
          <h2 className="dpreview__gate-title">
            {standalone ? 'Open deze deal in WalkIn' : 'Installeer WalkIn om deze deal te gebruiken'}
          </h2>
          {!standalone && (
            <p className="dpreview__gate-text">
              Deze deal is alleen geldig als je WalkIn op je telefoon hebt.
            </p>
          )}
          <ul className="dpreview__trust">
            <li>Volledig gratis</li>
            <li>Geen account nodig</li>
            <li>Volledig anoniem</li>
          </ul>
          <button className="dpreview__cta" onClick={handleInstallClick}>
            {standalone
              ? 'Open deze deal →'
              : 'Installeer WalkIn — Gratis →'}
          </button>
        </section>
      </main>

      {showInstall && (
        <div className="dpreview__modal" role="dialog" aria-modal="true">
          <div className="dpreview__modal-card">
            <h3 className="dpreview__modal-title">
              {ios
                ? 'WalkIn installeren op je iPhone:'
                : 'WalkIn installeren:'}
            </h3>
            <ol className="dpreview__modal-steps">
              {ios ? (
                <>
                  <li>Safari opent automatisch</li>
                  <li>Tik op het Delen icoon</li>
                  <li>Tik op "Zet op beginscherm"</li>
                  <li>Tik op "Voeg toe"</li>
                  <li>Open WalkIn en zoek de deal!</li>
                </>
              ) : (
                <>
                  <li>Open je browsermenu</li>
                  <li>Tik op "App installeren" of "Toevoegen aan startscherm"</li>
                  <li>Bevestig de installatie</li>
                  <li>Open WalkIn en zoek de deal!</li>
                </>
              )}
            </ol>
            <button
              className="dpreview__modal-cta"
              onClick={() => setShowInstall(false)}
            >
              Begrepen →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
